import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useDirectoryChildrenQuery } from '@/components/document/sub-components/file-manager/data/useDirectoryChildrenQuery';
import { isFolderLike, type FolderLikeItem } from '@/components/document/sub-components/file-manager/types';
import { isDescendantPath } from '@/components/document/sub-components/file-manager/utils';
import { sortDirectories } from '@/components/document/sub-components/file-manager/views/directory-list-view/sortDirectories';
import { useFileManagerNavigation } from './useFileManagerNavigation';

/**
 * Shared logic behind the sidebar tree's root listing (`DirectoryListView`) and every `DirectoryItem`
 * folder row below it: selection/expand state derived from the shared navigation state,
 * expand-on-click, and the single call site for `useDirectoryChildrenQuery`.
 *
 * Extracted after a real bug: the tree's root used to call `useDirectoryChildrenQuery('/', true)`
 * with a raw literal path, while every other row called it with a reactive getter. TanStack
 * Query let one observer's `queryFn` closure permanently attach to a different query object
 * because both queries transiently shared the `'/'` cache key at app boot. Funneling every row
 * through this one composable — which always calls the query with `() => toValue(path)` — makes
 * that kind of call-site drift structurally impossible going forward.
 */
export const useFileManagerTreeRow = (
  path: MaybeRefOrGetter<string>,
  options: {
    /**
     * When the caller already knows (from a parent listing) whether this folder has children,
     * pass it here: enables lazy fetching (only once expanded) and seeds the row's
     * `hasFolderChildren` guess until the real fetch resolves. That guess is drawn from Graph's
     * `folder.childCount` facet, which counts files and subfolders together — so it can say
     * "true" for a folder that turns out to hold only files. Once this row's own children have
     * actually been fetched (i.e. it's been expanded at least once), `hasFolderChildren` switches
     * to the real "does this contain any directories" fact and self-corrects, closing the row and
     * hiding its chevron if the guess was wrong. Omit this option for a row with no parent listing
     * to draw a guess from (the tree's root listing) — it then fetches eagerly and there's no guess
     * phase at all.
     */
    knownHasFolderChildren?: MaybeRefOrGetter<boolean>;
  } = {},
) => {
  const { knownHasFolderChildren } = options;
  const { currentPath, expandedPaths, toggleExpanded, navigateTo } =
    useFileManagerNavigation();

  const isSelected = computed(() => toValue(path) === currentPath.value);
  const isExpanded = computed(() => expandedPaths.value.has(toValue(path)));
  const isAncestorOfSelected = computed(
    () =>
      isDescendantPath(currentPath.value, toValue(path)) && !isExpanded.value,
  );

  const enabled = computed(() =>
    knownHasFolderChildren !== undefined
      ? toValue(knownHasFolderChildren) && isExpanded.value
      : true,
  );

  const { data: children, isLoading } = useDirectoryChildrenQuery(
    () => toValue(path),
    enabled,
  );

  const directories = computed(() =>
    sortDirectories(
      children.value?.filter(
        (item): item is FolderLikeItem => isFolderLike(item),
      ) ?? [],
    ),
  );

  const hasFolderChildren = computed(() =>
    children.value !== undefined
      ? directories.value.length > 0
      : knownHasFolderChildren !== undefined
        ? toValue(knownHasFolderChildren)
        : false,
  );

  const onItemClick = () => {
    navigateTo(toValue(path));
    if (hasFolderChildren.value && !isExpanded.value) {
      toggleExpanded(toValue(path));
    }
  };

  const onToggleExpand = () => {
    toggleExpanded(toValue(path));
  };

  return {
    isSelected,
    isExpanded,
    isAncestorOfSelected,
    hasFolderChildren,
    isLoading,
    directories,
    onItemClick,
    onToggleExpand,
  };
};
